import React, { Fragment } from 'react';
import { bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { intlShape } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { path, pathOr } from 'ramda';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import routeConfiguration from '../../../../routeConfiguration';
import { CONGRATS_ENQUIRY_PAGE } from '../../../../constants/page';
import { createResourceLocatorString } from '../../../../util/routes';
import { propTypes } from '../../../../util/types';
import * as validators from '../../../../util/validators';
import { formatFormValuesAsBookingData, getInitialValuesFromSearch } from '../../../../util/booking';
import { maskContactDetails } from '../../../../util/contactDetails';
import {parse, stringify} from '../../../../util/urlHelpers';
import { sendEnquiry } from '../../../../ducks/listing.duck';
import {
  Divider,
  FieldDateAndTimeInput,
  FieldParticipants,
  FieldTextInput,
  Form,
  PrimaryButton,
  UserCard,
} from '../../../../components';

import { handleTrackingTransaction } from '../../ContactPage.helpers';
import css from './ContactForm.module.css';
import {moment} from "../../../../util/dates";

const ContactForm = (props) => {
  const {
    className,
    author,
    currentUser,
    intl,
    listingId,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onSendEnquiry,
    publicData,
    sendEnquiryError,
    sendEnquiryInProgress,
    timeZone,
    slug,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const handleSubmit = (formValues) => {
    const bookingData = formatFormValuesAsBookingData(formValues);
    const {handleBookingParamsChange} = location;
    if (!handleBookingParamsChange) {
      const {search } = location;
      const searchParams = parse(search);
      const dateMaybe = bookingData?.bookingStart ? {
        bookingDate: moment(bookingData.bookingStart).set({ hour: 0 }).toISOString(),
        startTime: moment(bookingData.bookingStart).format('HH:mm'),
        endTime: moment(bookingData.bookingEnd).format('HH:mm'),
      } : {};
      const newSearchParams = {
        ...searchParams,
        ...dateMaybe,
      };
      const searchString = `?${stringify(newSearchParams)}`;
      history.push({
        pathname: 'l',
        search: searchString,
      });
      return;
    }

    // history.goBack();
    handleBookingParamsChange(bookingData, formValues);
  };
  const { enfantsBienvenue: childrenAllowed, maxCapacityToGuest = 0, minimumHoursPerBooking = 1 } = publicData;
  const authorDisplayName = path(['attributes', 'profile', 'displayName'], author);
  const searchParams = parse(location.search);
  const initialValues = getInitialValuesFromSearch(searchParams);
  const messageHeadingText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.messageHeading' });
  const messageInfoText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.messageInfo' });
  const messagePlaceholderText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.messagePlaceholder' }, { name: authorDisplayName });
  const messageLabelText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.messageLabel' });
  const messageRequiredText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.messageRequired' });
  const messageRequired = validators.required(messageRequiredText);
  const submitText = intl.formatMessage({ id: 'ListingChatBotPage.seePrice' });
  const sendEnquiryErrorText = intl.formatMessage({ id: 'ContactPage.EnquiryPanel.sendEnquiryError' });
  const classes = classNames(css.root, className);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={formRenderProps => {
        const { active, form, handleSubmit, values } = formRenderProps;
        const { adults, children } = values;

        return (
          <Fragment>
            <Form
              className={classes}
              id="contactForm"
              onSubmit={handleSubmit}
            >
              <div className={css.bookingSection}>
                <div className={css.dateTimeContainer}>
                  {monthlyTimeSlots && timeZone ? (
                    <div className={css.dateTimeContainer}>
                      <FieldDateAndTimeInput
                        className={css.fieldDateAndTimeInput}
                        activeField={active}
                        finalFormApi={form}
                        formValues={values}
                        listingId={listingId}
                        minimumHoursPerBooking={minimumHoursPerBooking}
                        monthlyTimeSlots={monthlyTimeSlots}
                        onFetchTimeSlots={onFetchTimeSlots}
                        timeZone={timeZone}
                      />
                    </div>
                  ) : null}
                </div>
                {values?.startTime && (
                  <div className={css.availableInfoContainer}>
                    <div className={css.availableCircle} /> {intl.formatMessage({
                    id: 'SlotAvailable',
                  })}
                  </div>
                )}
                {/*<FieldParticipants*/}
                {/*  className={css.fieldParticipants}*/}
                {/*  adultsValue={adults}*/}
                {/*  childrenAllowed={childrenAllowed}*/}
                {/*  childrenValue={children}*/}
                {/*  maxParticipants={maxCapacityToGuest}*/}
                {/*/>*/}
              </div>
              <Divider className={css.divider} />
              <div className={css.submitContainer}>
                {sendEnquiryError && <span className={css.error}>{sendEnquiryErrorText}</span>}
                <PrimaryButton
                  className={css.submitButton}
                  disabled={sendEnquiryInProgress}
                  inProgress={sendEnquiryInProgress}
                  type="submit"
                >
                  {submitText}
                </PrimaryButton>
              </div>
            </Form>
          </Fragment>
        )
      }}
    />
  )
}

ContactForm.defaultProps = {
  className: null,
  currentUser: null,
  sendEnquiryError: null,
  monthlyTimeSlots: null,
};

ContactForm.propTypes = {
  className: string,
  author: propTypes.user.isRequired,
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  listingId: object.isRequired,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onSendEnquiry: func.isRequired,
  publicData: object.isRequired,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
};

const mapDispatchToProps = dispatch => ({
  onSendEnquiry: (listingId, message, bookingData, searchParams) =>
    dispatch(sendEnquiry(listingId, message, bookingData, searchParams)),
});

const mapStateToProps = (state, props) => {
  const { id, sendEnquiryInProgress, sendEnquiryError } = state.listing;
  const { currentUser } = state.user;

  return {
    currentUser,
    listingId: id,
    sendEnquiryInProgress,
    sendEnquiryError,
  };
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
)(ContactForm);
